import * as Sentry from "@sentry/react";

/**
 * Helper function to format GraphQL errors for better Sentry reporting
 *
 * @param {Object} error - The error object (potentially a GraphQL error)
 * @param {Object} additionalContext - Additional context to include in Sentry
 * @returns {Object} - Formatted error and contexts for Sentry
 */
export const formatGraphQLError = (error, additionalContext = {}) => {
  // Check if this is a GraphQL error with errors array
  if (error?.errors && Array.isArray(error.errors)) {
    const errorMessages = error.errors.map(err => err.message).join('; ');
    const formattedError = new Error(`GraphQL Error: ${errorMessages}`);

    // Add full error details to Sentry context
    const graphqlContext = {
      graphql: {
        errors: error.errors.map(err => ({
          message: err.message,
          path: err.path,
          locations: err.locations
        })),
        data: error.data,
        ...additionalContext
      }
    };

    return { error: formattedError, contexts: graphqlContext };
  }

  // If it's a regular error, just return it with context
  return {
    error,
    contexts: additionalContext ? { additionalInfo: additionalContext } : {}
  };
};

/**
 * Captures and logs an error to Sentry with proper formatting
 *
 * @param {Object} error - The error to capture
 * @param {Object} options - Options for error capturing
 * @param {string} options.feature - Feature name (e.g., 'lecture-management')
 * @param {string} options.operation - Operation name (e.g., 'fetch-event-details')
 * @param {string} [options.consoleMessage] - Custom console error message
 * @param {Object} [options.context] - Additional context for Sentry
 * @param {Object} [options.tags] - Additional tags for Sentry
 */
export const captureError = (error, options = {}) => {
  const {
    feature,
    operation,
    consoleMessage,
    context = {},
    tags = {}
  } = options;

  // Log to console
  if (consoleMessage) {
    console.error(consoleMessage, error);
  } else {
    console.error(`Error in ${operation || 'unknown operation'}:`, error);
  }

  // Format error for better Sentry reporting
  const { error: formattedError, contexts } = formatGraphQLError(error, context);

  // Capture in Sentry
  Sentry.captureException(formattedError, {
    tags: {
      ...(feature && { feature }),
      ...(operation && { operation }),
      ...tags
    },
    contexts
  });
};
